import React, { useEffect, useRef, useState } from "react";
import PageSeo from "../seo/PageSeo";
import PageLayout from "../components/PageLayout";
import { embedBleedSx, embedIframeSx } from '../theme/pageLayout';
import { Box, Button, Fade, IconButton, Typography } from "@mui/material";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import useMediaQuery from '@mui/material/useMediaQuery';
import theme from '../Theme';
import { brandColors } from '../Theme';

const JOTFORM_PAGE_URL = 'https://form.jotform.com/251586631834160';
const JOTFORM_SRC =
    'https://form.jotform.com/251586631834160?width=100%25&background=1&align=Left';

/** Show back-to-top when the footer is this far below the viewport edge (px). */
const FOOTER_APPROACH_OFFSET = 280;
/** Show go-to-submit when within this distance from the page top (px). */
const TOP_SCROLL_THRESHOLD = 120;

const floatingActionSx = (visible: boolean) => ({
    display: { xs: 'flex', md: 'none' },
    position: 'fixed' as const,
    right: 16,
    bottom: 'max(20px, env(safe-area-inset-bottom))',
    zIndex: 1100,
    width: 44,
    height: 44,
    bgcolor: '#fff',
    color: brandColors.lavenderDark,
    boxShadow: '0 4px 16px rgba(15, 23, 42, 0.14)',
    border: '1px solid rgba(15, 23, 42, 0.08)',
    pointerEvents: visible ? 'auto' : 'none',
    '&:hover': {
        bgcolor: '#fff',
        color: brandColors.lavender,
    },
});

const PuppyApplication: React.FC = () => {
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [nearFooter, setNearFooter] = useState(false);
    const [nearTop, setNearTop] = useState(true);

    const showGoTop = nearFooter;
    const showGoSubmit = nearTop && !nearFooter;

    useEffect(() => {
        if (!isMobile) {
            setNearFooter(false);
            setNearTop(false);
            return;
        }

        const footer = document.querySelector('footer');
        if (!footer) return;

        const footerObserver = new IntersectionObserver(
            ([entry]) => setNearFooter(entry.isIntersecting),
            {
                root: null,
                rootMargin: `0px 0px ${FOOTER_APPROACH_OFFSET}px 0px`,
                threshold: 0,
            }
        );

        const onScroll = () => {
            setNearTop(window.scrollY <= TOP_SCROLL_THRESHOLD);
        };

        footerObserver.observe(footer);
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();

        return () => {
            footerObserver.disconnect();
            window.removeEventListener('scroll', onScroll);
        };
    }, [isMobile]);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    /** Scrolls the page to the bottom of the embed — submit lives inside the cross-origin iframe. */
    const scrollToSubmit = () => {
        const iframe = iframeRef.current;
        if (!iframe) return;

        const rect = iframe.getBoundingClientRect();
        const target = window.scrollY + rect.bottom - window.innerHeight + 56;
        window.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
    };

    return (
        <>
            <PageSeo
                title="Puppy Application | Galia Standard Poodles"
                description="Apply for a Galia Standard Poodle puppy in Austin, TX. Tell us about your family so we can match you with the right puppy."
                path="/puppy-application"
            />

            <PageLayout variant="embed">
                    <Typography component="h1" variant="h4" gutterBottom>
                        Puppy Application
                    </Typography>

                    <Typography variant="body1" paragraph sx={{ mb: 0 }}>
                        Owning a dog is a lifetime commitment. Our goal is to get to know your family and ensure the best possible match for both you and the puppy. Please fill out the form below to begin the process.
                    </Typography>

                    <Button
                        component="a"
                        href={JOTFORM_PAGE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="outlined"
                        startIcon={<OpenInNewIcon />}
                        sx={{
                            display: { xs: 'inline-flex', md: 'none' },
                            mt: 2,
                            mb: 0.5,
                            textTransform: 'none',
                            borderRadius: 3,
                            fontWeight: 600,
                        }}
                    >
                        Open form in browser
                    </Button>

                    <Box sx={embedBleedSx}>
                        <Box
                            ref={iframeRef}
                            component="iframe"
                            title="Puppy Application Form"
                            src={JOTFORM_SRC}
                            allowFullScreen
                            sx={embedIframeSx}
                        />
                    </Box>
            </PageLayout>

            <Fade in={showGoSubmit}>
                <IconButton
                    aria-label="Go to submit"
                    onClick={scrollToSubmit}
                    sx={floatingActionSx(showGoSubmit)}
                >
                    <KeyboardArrowDownIcon />
                </IconButton>
            </Fade>

            <Fade in={showGoTop}>
                <IconButton
                    aria-label="Back to top"
                    onClick={scrollToTop}
                    sx={floatingActionSx(showGoTop)}
                >
                    <KeyboardArrowUpIcon />
                </IconButton>
            </Fade>
        </>
    );
};

export default PuppyApplication;
